import { Cpu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ATELIERS_LIBRARY, useAtelierUnlock } from '../../hooks/useAtelierUnlock';
import { useCommercialCount } from '../../hooks/useCommercialCount';
import useGrandeEcole from '../../stores/useGrandeEcole';

const CREATORS = [
  {
    id: 'parcours',
    badge: 'PARCOURS_CREATOR',
    badgeColor: 'orange',
    title: 'Parcours Creator',
    description: "Construisez des parcours d'apprentissage complets — séquences pédagogiques, processus d'entreprise, chaînes opérationnelles, flux d'onboarding, certifications internes, trajectoires individuelles ou collectives.",
    route: '/admin/parcours-creator',
    outputPath: '/parcours/[slug]',
    meta: { label: 'Validation', value: 'Cross-protocol' },
  },
  {
    id: 'cases',
    badge: 'CASE_CREATOR',
    badgeColor: 'indigo',
    title: 'Case Creator',
    description: "Construisez des simulations avancées — réseaux bayésiens, contrefactuels, calibration génétique, graphes causaux, process mining, projections de bifurcation. Conçu pour l'analyse stratégique et la modélisation de systèmes complexes.",
    route: '/admin/cases-creator',
    outputPath: '/simulateur/[slug]',
    meta: { label: 'Moteur', value: 'Bayesian + Genetic' },
  },
  {
    id: 'ateliers',
    badge: 'ATELIER_CREATOR',
    badgeColor: 'pink',
    title: 'Atelier Creator',
    description: "Créez et gérez les ateliers accessibles aux apprenants — contenu théorique, exercices, ressources, sessions live. Chaque atelier est associé à un template de run via le système de déblocage.",
    route: '/admin/ateliers-creator',
    outputPath: '/ateliers/[slug]',
    meta: { label: 'Déblocage', value: 'Template-based' },
  },
  {
    id: 'vente',
    badge: 'COMMERCIAL_CREATOR',
    badgeColor: 'violet',
    title: 'Commercial Creator',
    description: "Concevez des scénarios de vente réalistes — simulations de rendez-vous client, jeux de rôle, préventes, gestion d'objections. Interface orientée vente directe, adaptable à la formation.",
    route: '/admin/commercial-creator',
    outputPath: '/simulateur/commercial/[slug]',
    meta: { label: 'Mode', value: 'Vente & Formation' },
  },
  
];

const COLOR_MAP = {
  orange: {
    badge:    'bg-orange-950/50 border-orange-800/30',
    dot:      'bg-orange-400',
    text:     'text-orange-300',
    glow:     'from-orange-600 to-red-600',
    border:   'hover:border-orange-500/30',
    stat:     'text-orange-400',
    path:     'text-orange-300',
    btn:      'hover:bg-orange-950/30 hover:border-orange-600/50 group-hover/btn:text-orange-300',
    btnArrow: 'group-hover/btn:text-orange-400',
    header:   'from-orange-600 to-orange-700 hover:from-orange-500 hover:to-orange-600 shadow-orange-900/20 hover:shadow-orange-900/40',
  },
  indigo: {
    badge:    'bg-indigo-950/50 border-indigo-800/30',
    dot:      'bg-indigo-400',
    text:     'text-indigo-300',
    glow:     'from-indigo-600 to-purple-600',
    border:   'hover:border-indigo-500/30',
    stat:     'text-indigo-400',
    path:     'text-indigo-300',
    btn:      'hover:bg-indigo-950/30 hover:border-indigo-600/50 group-hover/btn:text-indigo-300',
    btnArrow: 'group-hover/btn:text-indigo-400',
    header:   'from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 shadow-indigo-900/20 hover:shadow-indigo-900/40',
  },
  pink: {
    badge:    'bg-pink-950/50 border-pink-800/30',
    dot:      'bg-pink-400',
    text:     'text-pink-300',
    glow:     'from-pink-600 to-rose-600',
    border:   'hover:border-pink-500/30',
    stat:     'text-pink-400',
    path:     'text-pink-300',
    btn:      'hover:bg-pink-950/30 hover:border-pink-600/50 group-hover/btn:text-pink-300',
    btnArrow: 'group-hover/btn:text-pink-400',
    header:   'from-pink-600 to-pink-700 hover:from-pink-500 hover:to-pink-600 shadow-pink-900/20 hover:shadow-pink-900/40',
  },
  violet: {
    badge:    'bg-violet-950/50 border-violet-800/30',
    dot:      'bg-violet-400',
    text:     'text-violet-300',
    glow:     'from-violet-600 to-rose-600',
    border:   'hover:border-violet-500/30',
    stat:     'text-violet-400',
    path:     'text-violet-300',
    btn:      'hover:bg-violet-950/30 hover:border-violet-600/50 group-hover/btn:text-violet-300',
    btnArrow: 'group-hover/btn:text-violet-400',
    header:   'from-violet-600 to-violet-700 hover:from-violet-500 hover:to-violet-600 shadow-violet-900/20 hover:shadow-violet-900/40',
  },
};

export function ContentManager({ darkMode }) {
  const navigate = useNavigate();

  const courses   = useGrandeEcole(s => s.courses);
  const pesaCases = useGrandeEcole(s => s.pesaCases);
  const loading   = useGrandeEcole(s => s.loading);

  const { unlockedCount } = useAtelierUnlock();
  const ateliers = ATELIERS_LIBRARY;
  
  const { count: commercialCount, loading: commercialLoading } = useCommercialCount();

  // Stats dynamiques par creator
  const STATS = {
    parcours: { count: loading ? '…' : courses.length,   label: 'Parcours' },
    cases:    { count: loading ? '…' : pesaCases.length, label: 'Cases'    },    
    ateliers: { count: ateliers.length,                   label: 'Ateliers' },
    vente:  { count: commercialLoading ? '…' : commercialCount, label: 'Scénarios' }, // nouveau
};

  const STAT2 = {
    ateliers: { count: unlockedCount, label: 'Débloqués', color: 'text-emerald-400' },
  };



  return (
    <div className="space-y-10 relative">

      {/* Ambient blobs */}
      <div className="fixed inset-0 pointer-events-none opacity-30">
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-[120px] animate-pulse"
          style={{ animationDuration: '8s' }}
        />
        <div
          className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-[120px] animate-pulse"
          style={{ animationDuration: '12s', animationDelay: '2s' }}
        />
      </div>

      {/* ── Header ── */}
      <header className="relative">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-8">
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-4 bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 rounded-2xl hover:border-orange-500/30 transition-all duration-300">
                  <Cpu size={28} className="text-orange-400" strokeWidth={1.5} />
                </div>
              </div>
              <div>
                <h2
                  className="text-2xl md:text-4xl font-light tracking-tight text-white mb-1"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif', letterSpacing: '-0.03em' }}
                >
                  PESACORE Architect
                </h2>
                <p className="text-sm font-light text-zinc-500 tracking-wide">
                  Création de Contenu · Compilation · Noyau de Simulation
                </p>
              </div>
            </div>
            <p className="text-base text-zinc-400 font-light leading-relaxed max-w-2xl">
              Ingénierie de protocoles avancés pour parcours d'apprentissage et frameworks de simulation décisionnelle.
              Construisez des expériences structurées ou des modèles comportementaux complexes avec compilation validée.
            </p>
          </div>

          {/* CTA rapides */}
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            {CREATORS.map(c => {
              const col = COLOR_MAP[c.badgeColor];
              return (
                <button
                  key={c.id}
                  onClick={() => navigate(c.route)}
                  className={`group relative px-5 py-2.5 bg-gradient-to-b ${col.header} text-white text-xs font-medium rounded-xl transition-all duration-300 shadow-lg hover:-translate-y-0.5 w-full sm:w-auto flex items-center justify-center gap-2`}
                >
                  {c.title.split(' ')[0]}
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className="opacity-70 group-hover:translate-x-0.5 transition-transform">
                    <path d="M1 7h12M7 1l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* ── Cards creators ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
        {CREATORS.map(creator => {
          const col  = COLOR_MAP[creator.badgeColor];
          const stat = STATS[creator.id];
          const stat2 = STAT2[creator.id] ?? null;

          return (
            <article key={creator.id} className="group relative">
              {/* Glow */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${col.glow} rounded-2xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-500`} />

              <div className={`relative bg-gradient-to-br from-zinc-900/90 to-zinc-950/90 backdrop-blur-xl border border-zinc-800 ${col.border} rounded-2xl p-8 transition-all duration-500 flex flex-col h-full`}>

                {/* Badge */}
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 ${col.badge} border rounded-full mb-6 self-start`}>
                  <div className={`w-1.5 h-1.5 ${col.dot} rounded-full animate-pulse`} />
                  <span className={`text-xs font-medium ${col.text} tracking-wide`}>{creator.badge}</span>
                </div>

                {/* Titre */}
                <h3
                  className="text-2xl font-light text-white mb-4 tracking-tight"
                  style={{ letterSpacing: '-0.02em' }}
                >
                  {creator.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-zinc-400 font-light leading-relaxed mb-8 flex-1">
                  {creator.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8 pb-8 border-b border-zinc-800/50">
                  {/* Compteur principal */}
                  <div className="space-y-1">
                    <div className={`text-2xl font-light ${col.stat}`}>{stat.count}</div>
                    <div className="text-xs text-zinc-500 font-medium tracking-wide">{stat.label}</div>
                  </div>

                  {/* Stat secondaire (débloqués pour ateliers, sinon meta) */}
                  <div className="space-y-1">
                    {stat2 ? (
                      <>
                        <div className={`text-2xl font-light ${stat2.color}`}>{stat2.count}</div>
                        <div className="text-xs text-zinc-500 font-medium tracking-wide">{stat2.label}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-white font-medium">{creator.meta.value}</div>
                        <div className="text-xs text-zinc-500 font-medium tracking-wide">{creator.meta.label}</div>
                      </>
                    )}
                  </div>

                  {/* Output path */}
                  <div className="space-y-1">
                    <div className={`text-xs ${col.path} font-mono break-all`}>{creator.outputPath}</div>
                    <div className="text-xs text-zinc-500 font-medium tracking-wide">Sortie</div>
                  </div>
                </div>

                {/* CTA */}
                <button
                  onClick={() => navigate(creator.route)}
                  className={`group/btn w-full flex items-center justify-between px-5 py-3 bg-zinc-800/50 ${col.btn} border border-zinc-700/50 rounded-xl transition-all duration-300`}
                >
                  <span className={`text-sm font-medium text-zinc-300 transition-colors`}>
                    Lancer le Créateur
                  </span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={`text-zinc-500 ${col.btnArrow} group-hover/btn:translate-x-1 transition-all`}>
                    <path d="M1 8h14M8 1l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {/* ── Footer status bar ── */}
      <footer className="relative">
        <div className="flex flex-wrap items-center justify-between px-6 py-4 bg-zinc-900/50 border border-zinc-800/50 rounded-2xl backdrop-blur-sm gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">

            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-zinc-500 font-medium">Moteur</span>
              <span className="text-emerald-400 font-medium">Liaison Établie</span>
            </div>

            <div className="h-4 w-px bg-zinc-800 hidden sm:block" />

            {[
              { label: 'Parcours', value: STATS.parcours.count, color: 'text-orange-400' },
              { label: 'Cases',    value: STATS.cases.count,    color: 'text-indigo-400' },
              { label: 'Ateliers', value: STATS.ateliers.count, color: 'text-pink-400'   },
              { label: 'Vente', value: STATS.vente.count, color: 'text-violet-400' },
              { label: 'Débloqués', value: unlockedCount,       color: 'text-emerald-400' },
            ].map(({ label, value, color }, i) => (
              <div key={label} className="flex items-center gap-2">
                {i > 0 && <div className="h-4 w-px bg-zinc-800" />}
                <span className="text-zinc-500 font-medium">{label}</span>
                <span className={`${color} font-semibold`}>{value}</span>
              </div>
            ))}

          </div>
          <div className="text-xs text-zinc-600 font-mono">v2.4.1</div>
        </div>
      </footer>
    </div>
  );
}

export default ContentManager;